import { I18N } from "oc-I18N";
import { useAiChat, formatCode, getCurrentUIState } from "./aiConfig";
import { createMessage as createMessageLight } from "btg-ai-agent";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import * as Commons from "oc-commons/Commons";
import { sleep } from "oc-commons/Commons";
import { useLocalStorage } from "oc-ui/react/useLocalStorage";
import Markdown from "markdown-to-jsx";
import { createPortal } from "react-dom";
import { MFAQ } from "../../../metamodell";
import { actions } from "../simpleAssistant/SimpleAssistant";
import { useSpeechToText, useTextToSpeech } from "./useSpeech";

const getFAQEntry = Commons.cache(
  (key): MFAQ => APP.richMetadata["findFAQ"](key) || _.minBy(APP.metadata.FAQ, (x) => x.id)
);

// Module-level state: survives component re-mounts on page switches
let _persistedOpenState: string | null = null;

/**
 * Tracks message IDs that have already been animated (pop-in).
 * Persists across component re-mounts (page switches) so that existing
 * messages do NOT replay their entrance animation when switching pages.
 */
const _animatedMessageIds = new Set<string>();

export function ChatBot({
  initialChatMessages,
  initialOpen = false,
  faqRootKey,
  showInternalMessages,
}: {
  avatarMessage?: string;
  initialChatMessages?: string[];
  initialOpen?: boolean;
  faqRootKey?: string;
  /** Interne Nachrichten (Log-Einträge, Code) anzeigen. Standard: aus APP.settings.ai.showInternalMessages (fallback: false) */
  showInternalMessages?: boolean;
} = {}) {
  const isTest = new URL(document.location.href).searchParams.has("test");
  const tinaParam = new URL(document.location.href).searchParams.get("tina");
  const noAssistant =
    new URL(document.location.href).searchParams.get("tutorial") == "false" && tinaParam !== "true";
  if (!APP.settings.tina.active || isTest || noAssistant) return <></>;

  const effectiveShowInternalMessages =
    APP.settings.ai?.showInternalMessages ?? showInternalMessages ?? false;

  const [openState, setOpenStateRaw] = useState<string>(
    _persistedOpenState ?? (initialOpen ? "open" : "closed")
  );
  const setOpenState = (state: string) => {
    _persistedOpenState = state;
    setOpenStateRaw(state);
  };

  const [hiddenMessageCount, setHiddenMessageCount] = useState(0);

  useEffect(() => {
    const onMessagesChanged = () => {
      if (APP.messages?.state.hidden) {
        setHiddenMessageCount(APP.messages.state.messages.length);
      } else {
        setHiddenMessageCount(0);
      }
    };

    // Check initial
    onMessagesChanged();

    APP.on("messagesChanged", onMessagesChanged);
    return () => {
      APP.off("messagesChanged", onMessagesChanged);
    };
  }, []);

  const hidden =
    APP.settings.startupCode && Commons.isEmpty(APP.pageNameFromParam)
      ? !document.querySelector(".assistant-container-start")?.classList.contains("move-to-final")
      : false;

  const openChatFunction =
    openState == "closed"
      ? () => {
          setOpenState("open");
          APP.logUserAction({ action: "openTinaChatWindow" }, { stateChange: false });
        }
      : undefined;

  const handleBadgeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    APP.messages?.setHidden(false);
  };

  return (
    <div
      key="Assistant"
      data-button-key="openChatbot"
      className={"assistant-container page-fade-out-on-exit" + (hidden ? " hidden" : "")}
      role="complementary"
      aria-label={I18N("aria::Chatbot-Assistent")}
    >
      <div className="assistant-message-and-button">
        {/*<div*/}
        {/*  className={"assistant-message shadow-light" + (showMessagesState ? "" : " minimize")}*/}
        {/*  dangerouslySetInnerHTML={{ __html: avatarMessage }}*/}
        {/*  role="status"*/}
        {/*  aria-live="polite"*/}
        {/*  aria-atomic="true"*/}
        {/*></div>*/}
        <button
          className="assistant-avatar assistant-avatar-button hover-shadow shadow-light"
          title={initialChatMessages[0]}
          aria-label={openState === "closed" ? I18N("aria::Chat öffnen") : initialChatMessages[0]}
          onClick={openChatFunction}
          disabled={!openChatFunction}
          tabIndex={0}
        >
          {hiddenMessageCount > 0 && (
            <div
              className="messages-badge"
              onClick={handleBadgeClick}
              role="button"
              aria-label={I18N("Nachrichten anzeigen")}
            >
              {hiddenMessageCount}
            </div>
          )}
        </button>
      </div>
      <div
        className="assistant-message-actions"
        role="navigation"
        aria-label={I18N("Chatbot-Aktionen")}
      >
        {/*<button*/}
        {/*  className={"assistant-message-action shadow-light" + (showMessagesState ? "" : " hidden")}*/}
        {/*  onClick={hideMessages}*/}
        {/*  aria-label={I18N("Hinweis ausblenden")}*/}
        {/*  tabIndex={0}*/}
        {/*>*/}
        {/*  <i aria-hidden="true">✖️</i>*/}
        {/*  <span className={showMessagesState ? "" : "minimize"}>{I18N("Hinweis ausblenden")}</span>*/}
        {/*</button>*/}
        <button
          className="assistant-message-action shadow-light"
          onClick={openChatFunction}
          disabled={!openChatFunction}
          aria-label={I18N("Ich habe eine Frage")}
          tabIndex={0}
        >
          <i aria-hidden="true">❔</i>
          <span className={/*showMessagesState ? "" :*/ "minimize"}>
            {I18N("Ich habe eine Frage")}
          </span>
        </button>
      </div>
      <ChatBotPopup
        {...{
          openState,
          setOpenState,
          initialChatMessages,
          faqRootKey,
          showInternalMessages: effectiveShowInternalMessages,
        }}
      />
    </div>
  );
}

function ChatBotPopup({
  initialChatMessages,
  openState,
  setOpenState,
  faqRootKey,
  showInternalMessages = true,
}: {
  initialChatMessages: string[];
  openState: string;
  setOpenState: (o: string) => void;
  faqRootKey: string;
  showInternalMessages?: boolean;
}) {
  const chatBodyRef = useRef<HTMLDivElement>(null);
  const chatBotRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Resize logic ──────────────────────────────────────────────────────────
  const MIN_W = 300;
  const MIN_H = 300;
  const MAX_W = typeof window !== "undefined" ? window.innerWidth - 32 : 9999;
  const MAX_H = typeof window !== "undefined" ? window.innerHeight - 32 : 9999;
  const [panelSize, setPanelSize] = useLocalStorage<{ w: number; h: number }>(
    "chatbot-panel-size",
    { w: 380, h: 560 }
  );
  const dragRef = useRef<{ startX: number; startY: number; startW: number; startH: number } | null>(
    null
  );

  const onResizePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        startW: panelSize.w,
        startH: panelSize.h,
      };
    },
    [panelSize]
  );

  const onResizePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!dragRef.current) return;
      const dx = dragRef.current.startX - e.clientX; // nach links ziehen → breiter
      const dy = dragRef.current.startY - e.clientY; // nach oben ziehen → höher
      setPanelSize({
        w: Math.max(MIN_W, Math.min(MAX_W, dragRef.current.startW + dx)),
        h: Math.max(MIN_H, Math.min(MAX_H, dragRef.current.startH + dy)),
      });
    },
    [MAX_W, MAX_H]
  );

  const onResizePointerUp = useCallback(() => {
    dragRef.current = null;
  }, []);

  const defaultModel = APP.settings.ai?.model;
  const availableModels: string[] =
    (APP.settings.ai as any)?.modelOptions ??
    (APP.metamodel as any)?.settings?.find?.((s: any) => s.Key === "ai.model")?.selectOptions ??
    [];
  const [activeModel, setActiveModel] = useLocalStorage<string>(
    "chatbot-active-model",
    defaultModel
  );
  const { postMessage, messages, setMessages, isLoading, error, cancel, activeModelName } =
    useAiChat(activeModel);

  // Sync the select when the agent switches to vision model internally
  useEffect(() => {
    if (activeModelName && activeModelName !== activeModel) {
      setActiveModel(activeModelName);
    }
  }, [activeModelName]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Feature flags from settings ────────────────────────────────────────────
  const showModelSwitch = !!(APP.settings.ai as any)?.allowModelSwitch;
  const showExportChat = !!(APP.settings.ai as any)?.showExportChat;
  const showDisclaimer = (APP.settings.ai as any)?.showDisclaimer !== false;
  const [disclaimerDismissed, setDisclaimerDismissed] = useLocalStorage<boolean>(
    "chatbot-disclaimer-dismissed",
    false
  );

  // ── Export chat ────────────────────────────────────────────────────────────
  const handleExportChat = () => {
    const lines: string[] = [
      `# Chat Export – ${new Date().toLocaleString()}`,
      `Modell: ${activeModel}`,
      `Seite: ${APP.pageName}`,
      "",
      "## Nachrichten",
      "",
    ];

    const roleLabel: Record<string, string> = {
      user: "Nutzer",
      assistant: "Assistent",
      log: "Log",
      code: "Code",
      error: "Fehler",
    };

    for (const msg of messages) {
      const label = roleLabel[msg.role] ?? msg.role;
      const text = msg.content
        .filter((c) => c.type === "text")
        .map((c) => c.text.value)
        .join("\n");
      if (!text.trim()) continue;
      const isCode = msg.role === "code" || msg.role === "log";
      lines.push(`**${label}:**`, isCode ? "```js" : "", text, isCode ? "```" : "", "");
    }

    try {
      lines.push("---", "## UI-State", "");
      lines.push("```json", JSON.stringify(getCurrentUIState(), null, 2), "```", "");
    } catch (_) {}

    try {
      lines.push("---", "## Konfiguration", "");
      lines.push("```json", JSON.stringify(APP.arrangement.toJSON?.(), null, 2), "```");
    } catch (_) {}

    const blob = new Blob([lines.join("\n")], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `chat-export-${Date.now()}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const [pendingImage, setPendingImage] = useState<string | null>(null);

  const MAX_DIMENSION = 2048;
  const JPEG_QUALITY = 0.85;

  /** Resize an image via Canvas so the longest edge is at most MAX_DIMENSION px. */
  const resizeImage = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
          const scale = MAX_DIMENSION / Math.max(width, height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d")!;
        ctx.drawImage(img, 0, 0, width, height);
        // Use JPEG for photos (smaller), keep PNG for transparent images
        const isPng = file.type === "image/png";
        resolve(canvas.toDataURL(isPng ? "image/png" : "image/jpeg", JPEG_QUALITY));
      };
      img.onerror = () => reject(new Error("Bild konnte nicht geladen werden"));
      img.src = URL.createObjectURL(file);
    });

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith("image/")) return;
    try {
      const dataUrl = await resizeImage(file);
      setPendingImage(dataUrl);
    } catch (err) {
      console.error("Image resize failed:", err);
    }
    // Reset so the same file can be selected again
    e.target.value = "";
  };

  const removePendingImage = () => {
    setPendingImage(null);
  };

  const faqRoot = getFAQEntry(faqRootKey);
  const options = faqRoot.Unterpunkte.map((unterpunkt, i) => {
    const optionClickedCallback = () => {
      if (unterpunkt.Aktion == "Textanzeige") {
        setMessages((messages) => {
          return [
            ...messages,
            createMessageLight("user", [unterpunkt.Titel]),
            createMessageLight("assistant", [unterpunkt.Text]),
          ];
        });
      } else {
        setOpenState("closing");
        setTimeout(() => {
          actions[unterpunkt.Aktion]?.();
        }, 300);
      }
    };
    const opt = createMessageLight("option", [unterpunkt.Titel], null, optionClickedCallback);
    // Stable ID based on content so the animation is not replayed on re-renders
    return { ...opt, id: `__option_${i}_${unterpunkt.Titel}` };
  });

  const combinedMessages = (
    initialChatMessages.length == 0
      ? []
      : [
          {
            ...createMessageLight("assistant", initialChatMessages, 1),
            // Stable ID so the initial greeting is never re-animated
            id: "id-chatbot-initial",
          },
        ]
  )
    .concat(messages)
    .concat(APP.settings.ai?.active ? [] : options);

  const INTERNAL_ROLES = new Set(["log", "code", "error"]);
  const visibleMessages = showInternalMessages
    ? combinedMessages
    : combinedMessages.filter((m) => !INTERNAL_ROLES.has(m.role));

  // After each render, register all visible message IDs as already-animated
  // so they are not re-animated on the next render (e.g. after a page switch).
  useEffect(() => {
    visibleMessages.forEach(({ id }) => {
      if (id) _animatedMessageIds.add(id);
    });
  });

  const [hoveredImageUrl, setHoveredImageUrl] = useState<string | null>(null);

  // ── Speech ────────────────────────────────────────────────────────────────
  const speechLang = APP.settings.lang === "fr" ? "fr-FR" : APP.settings.lang === "en" ? "en-US" : APP.settings.lang === "nl" ? "nl-NL" : "de-DE";
  const stt = useSpeechToText(speechLang);
  const tts = useTextToSpeech(speechLang);
  const [autoSpeak, setAutoSpeak] = useLocalStorage<boolean>("chatbot-auto-speak", false);

  // Auto-speak latest assistant message
  const lastAssistantMsg = useRef<string | null>(null);
  useEffect(() => {
    if (!autoSpeak || !tts.supported) return;
    const assistantMessages = messages.filter((m) => m.role === "assistant");
    if (assistantMessages.length === 0) return;
    const latest = assistantMessages[assistantMessages.length - 1];
    const text = latest.content
      .filter((c) => c.type === "text")
      .map((c) => c.text?.value ?? "")
      .join(" ")
      .trim();
    if (text && text !== lastAssistantMsg.current && !isLoading) {
      lastAssistantMsg.current = text;
      tts.speak(text);
    }
  }, [messages, isLoading, autoSpeak]);

  const handleVoiceResult = useCallback(
    (text: string) => {
      if (textareaRef.current) {
        const existing = textareaRef.current.value;
        textareaRef.current.value = existing ? existing + " " + text : text;
      }
    },
    []
  );

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const post = () => {
    const textArea = textareaRef?.current;
    const text = textArea?.value;
    if (text != null || pendingImage) {
      if (textArea) textArea.value = "";
      const image = pendingImage || undefined;
      setPendingImage(null);
      postMessage(text || "", image);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !(e.shiftKey || e.ctrlKey)) {
      e.preventDefault();
      post();
    }
  };

  useEffect(() => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scroll({ top: chatBodyRef.current.scrollHeight, behavior: "smooth" });
      setTimeout(() => {
        chatBodyRef.current?.scroll({ top: chatBodyRef.current.scrollHeight, behavior: "smooth" });
      }, 1500);
    }
  }, [messages, openState]);

  useEffect(() => {
    sleep(300).then(() => {
      if (textareaRef.current && openState == "open") {
        textareaRef.current.focus();
      }
    });
  }, [messages, openState]);

  useEffect(() => {
    const handleTransitionEnd = (event: TransitionEvent) => {
      if (event.propertyName === "font-size" && openState == "closing") {
        setOpenState("closed");
      }
    };

    const chatBotElement = chatBotRef.current;
    chatBotElement?.addEventListener("transitionend", handleTransitionEnd);

    return () => {
      chatBotElement?.removeEventListener("transitionend", handleTransitionEnd);
    };
  }, [openState]);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && openState === "open") {
        setOpenState("closing");
        APP.logUserAction({ action: "closeTinaChatWindow" }, { stateChange: false });
      }
    };

    if (openState === "open") {
      document.addEventListener("keydown", handleEscapeKey);
    }

    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [openState]);

  const chatbotElement = (
    <div
      ref={chatBotRef}
      className={`chatbot ${openState} page-fade-out-on-exit`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="chatbot-title"
      aria-describedby="chatbot-description"
      style={
        openState !== "closed"
          ? { width: panelSize.w, height: panelSize.h, maxWidth: "none", maxHeight: "none" }
          : undefined
      }
    >
      {/* Resize-Handle – obere linke Ecke */}
      {openState !== "closed" && (
        <div
          className="chatbot-resize-handle"
          onPointerDown={onResizePointerDown}
          onPointerMove={onResizePointerMove}
          onPointerUp={onResizePointerUp}
          onPointerCancel={onResizePointerUp}
        />
      )}
      <div className="chatbot-header">
        <div className="chatbot-header-row">
          <div className="assistant-avatar" role="img" aria-label={I18N("aria::Tina Avatar")}></div>
          <h2 id="chatbot-title" className="chatbot-title">
            {I18N("Tina")}
          </h2>
          <span id="chatbot-description" className="sr-only">
            {I18N("aria::Chat-Assistent für Ihre Fragen")}
          </span>
          <button
            className="close"
            onClick={() => {
              setOpenState("closing");
              APP.logUserAction({ action: "closeTinaChatWindow" }, { stateChange: false });
            }}
            aria-label={I18N("aria::Chat schließen")}
            tabIndex={0}
          ></button>
        </div>
        {APP.settings.ai?.active &&
        ((showModelSwitch && availableModels.length > 0) ||
          (showExportChat && messages.length > 0)) ? (
          <div className="chatbot-export-bar">
            {showModelSwitch && availableModels.length > 0 && (
              <label className="chatbot-model-switch">
                <span>{I18N("Chatbot-Modell")}</span>
                <select
                  value={activeModel}
                  onChange={(e) => setActiveModel(e.target.value)}
                  aria-label={I18N("aria::Chatbot-Modell")}
                >
                  {availableModels.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {showExportChat && messages.length > 0 && (
              <button
                className="chatbot-export-link"
                onClick={handleExportChat}
                aria-label={I18N("aria::Chat exportieren")}
                type="button"
              >
                ⬇ {I18N("Chat exportieren")}
              </button>
            )}
          </div>
        ) : null}
      </div>
      <div
        className="chatbot-body"
        ref={chatBodyRef}
        role="log"
        aria-live="polite"
        aria-relevant="additions"
        aria-label={I18N("aria::Chat-Verlauf")}
      >
        {visibleMessages.map(({ role, content, messageIndex, id }, index) => {
          const isNew = id == null || !_animatedMessageIds.has(id);
          return (
            <div
              key={id ?? index}
              className={`chatbot-message role-${role}${isNew ? " pop-in-delay" : ""}`}
              role={role === "assistant" ? "status" : undefined}
              aria-label={
                role === "user"
                  ? I18N("aria::Ihre Nachricht")
                  : role === "assistant"
                    ? I18N("aria::Antwort von Tina")
                    : undefined
              }
            >
              {content
                .filter((message) => message.type === "image" && message.imageData)
                .map((message, imgIndex) => (
                  <img
                    key={`img-${messageIndex}-${imgIndex}`}
                    src={message.imageData}
                    alt={I18N("Hochgeladenes Bild")}
                    className="chatbot-message-image"
                    onMouseEnter={() => setHoveredImageUrl(message.imageData!)}
                    onMouseLeave={() => setHoveredImageUrl(null)}
                    style={{ cursor: "zoom-in" }}
                  />
                ))}
              {content
                .filter((message) => message.type == "text" || message.type == "option")
                .map((message, contentIndex) => (
                  <Fragment key={messageIndex + "." + contentIndex}>
                    {message.type == "option" ? (
                      <button className="primary-button" onClick={message.callback}>
                        {message.text?.value}
                      </button>
                    ) : role == "log" || role == "code" ? (
                      <pre className="chatbot-code-block">
                        <code>
                          {role == "code" ? formatCode(message.text?.value) : message.text?.value}
                        </code>
                      </pre>
                    ) : (
                      <>
                        <Markdown children={message.text?.value} />
                        {message.text?.annotations?.length > 0 && (
                          <>
                            {message.text.annotations.map((annotation, index) => (
                              <span key={index}>{JSON.stringify(annotation)}</span>
                            ))}
                          </>
                        )}
                      </>
                    )}
                  </Fragment>
                ))}
            </div>
          );
        })}
        {isLoading && (
          <div
            key={"loading"}
            className={`chatbot-message role-assistant show-delayed`}
            role="status"
            aria-live="polite"
            aria-label={I18N("aria::Tina antwortet...")}
          >
            <div className="loading-indicator" aria-hidden="true">
              <span>.</span>
              <span>.</span>
              <span>.</span>
            </div>
          </div>
        )}
        {error && (
          <div
            key={"error"}
            className="chatbot-message role-error pop-in"
            role="alert"
            aria-live="assertive"
          >
            {I18N("Verbindungsfehler:")}
            <br />
            {error}
          </div>
        )}
      </div>
      {/* Image Hover Tooltip */}
      {hoveredImageUrl && (
        <div
          className="chatbot-image-tooltip"
          onMouseLeave={() => setHoveredImageUrl(null)}
          role="img"
          aria-label={I18N("Vergrößerte Bildvorschau")}
        >
          <img src={hoveredImageUrl} alt={I18N("Vergrößertes Bild")} />
        </div>
      )}
      {APP.settings.ai?.active && (
        <div className="chatbot-footer" role="form" aria-label={I18N("aria::Nachricht senden")}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="sr-only"
            tabIndex={-1}
            onChange={handleImageUpload}
            aria-hidden="true"
          />
          {pendingImage && (
            <div className="image-preview-bar">
              <div className="image-preview-item">
                <img src={pendingImage} alt={I18N("Angehängtes Bild")} />
                <button
                  className="image-preview-remove"
                  onClick={removePendingImage}
                  aria-label={I18N("Bild entfernen")}
                  type="button"
                >
                  ×
                </button>
              </div>
            </div>
          )}
          <div className="chatbot-input-row">
            <label htmlFor="chatbot-input" className="sr-only">
              {I18N("aria::Ihre Nachricht")}
            </label>
            <textarea
              id="chatbot-input"
              ref={textareaRef}
              autoFocus={true}
              onKeyDown={onKeyDown}
              aria-label={I18N("aria::Nachricht eingeben")}
              aria-describedby="chatbot-input-help"
              placeholder={
                pendingImage ? I18N("Beschreiben Sie das Bild...") : I18N("aria::Ihre Nachricht...")
              }
            />
            <span id="chatbot-input-help" className="sr-only">
              {I18N("aria::Drücken Sie Enter zum Senden oder Shift+Enter für eine neue Zeile")}
            </span>
            <button
              className="image-upload-button"
              type="button"
              title={I18N("Bild hochladen")}
              aria-label={I18N("Bild hochladen")}
              onClick={() => fileInputRef.current?.click()}
              tabIndex={0}
            >
              📎
            </button>
            {stt.supported && (
              <button
                className={`voice-button${stt.isListening ? " voice-active" : ""}`}
                type="button"
                title={stt.isListening ? I18N("Spracheingabe stoppen") : I18N("Spracheingabe")}
                aria-label={stt.isListening ? I18N("Spracheingabe stoppen") : I18N("Spracheingabe")}
                onClick={() => stt.toggleListening(handleVoiceResult)}
                tabIndex={0}
              >
                🎤
              </button>
            )}
            {tts.supported && (
              <button
                className={`voice-button${autoSpeak ? " voice-active" : ""}`}
                type="button"
                title={autoSpeak ? I18N("Sprachausgabe deaktivieren") : I18N("Sprachausgabe aktivieren")}
                aria-label={autoSpeak ? I18N("Sprachausgabe deaktivieren") : I18N("Sprachausgabe aktivieren")}
                onClick={() => {
                  if (autoSpeak) tts.stopSpeaking();
                  setAutoSpeak(!autoSpeak);
                }}
                tabIndex={0}
              >
                {autoSpeak ? "🔊" : "🔇"}
              </button>
            )}
            {isLoading ? (
              <button
                key={"cancel"}
                className={`primary-button button-bot button-cancel`}
                title={I18N("cancel")}
                aria-label={I18N("aria::Abbrechen")}
                onClick={cancel}
                tabIndex={0}
              />
            ) : (
              <button
                key={"send"}
                className={`primary-button button-bot button-send`}
                title={I18N("send")}
                aria-label={I18N("aria::Nachricht senden")}
                onClick={post}
                tabIndex={0}
              />
            )}
          </div>
          {APP.settings.ai?.active && showDisclaimer && !disclaimerDismissed && (
            <p className="chatbot-disclaimer">
              {I18N("KI-Disclaimer")}
              <button
                className="chatbot-disclaimer-dismiss"
                onClick={() => setDisclaimerDismissed(true)}
                aria-label={I18N("aria::Chat schließen")}
                type="button"
              >
                ×
              </button>
            </p>
          )}
        </div>
      )}
    </div>
  );
  return createPortal(chatbotElement as any, document.querySelector("#cfg-app"));
}
